/* ============================================================================
   Recesso — online withdrawal function (art. 54-bis Cod. Cons., D.Lgs. 209/2025)
   Vanilla JS, no dependencies, no analytics/marketing, first-party same-origin
   fetch only. No dependency on the consent/GDPR banner. Loaded as an external
   asset (CSP-safe). All dynamic config is read from data-* attributes on the
   section root; all i18n strings come from the in-page <script class="recesso-i18n">.
   Nothing user- or server-controlled is ever inserted as HTML — DOM is built
   with createElement + textContent only.
   ============================================================================ */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var FETCH_TIMEOUT_MS = 30000;

  function initRecesso(root) {
    var endpoint = root.getAttribute('data-endpoint') || '/apps/recesso';
    var locale = root.getAttribute('data-locale') === 'it' ? 'it' : 'en';
    var supportEmail = root.getAttribute('data-support-email') || 'contact@balanceboard.pro';

    var i18nNode = root.querySelector('script.recesso-i18n');
    var STR = {};
    try { STR = (JSON.parse(i18nNode.textContent) || {})[locale] || {}; } catch (e) { STR = {}; }

    var step1 = root.querySelector('#recesso-step1');
    var step2 = root.querySelector('#recesso-step2');
    var successPanel = root.querySelector('#recesso-success');
    var errorPanel = root.querySelector('#recesso-error');
    if (!step1 || !step2 || !successPanel || !errorPanel) return;

    var nameEl = root.querySelector('#recesso-name');
    var orderEl = root.querySelector('#recesso-order');
    var emailEl = root.querySelector('#recesso-email');
    var statementEl = root.querySelector('#recesso-statement');

    var step1Error = root.querySelector('#recesso-step1-error');
    var step2Error = root.querySelector('#recesso-step2-error');
    var confirmBtn = root.querySelector('#recesso-confirm');
    var backBtn = root.querySelector('#recesso-back');

    var FIELD_MAP = { name: 'recesso-name', orderNumber: 'recesso-order', email: 'recesso-email', statement: 'recesso-statement' };

    var draftToken = null;
    var pending = false;

    function t(key, vars) {
      var s = STR[key] || '';
      if (vars) {
        Object.keys(vars).forEach(function (k) {
          s = s.split('{' + k + '}').join(vars[k]); // replace ALL occurrences
        });
      }
      return s;
    }

    function show(panel) {
      [step1, step2, successPanel, errorPanel].forEach(function (p) { if (p) p.hidden = (p !== panel); });
      // Confirm control is reachable ONLY while step 2 is the active panel.
      if (confirmBtn) confirmBtn.disabled = (panel !== step2);
      var h = panel.querySelector('[tabindex="-1"], h1, h2');
      if (h) { try { h.focus(); } catch (e) {} }
    }

    function setFieldError(id, msg) {
      var el = root.querySelector('#' + id + '-err');
      var input = root.querySelector('#' + id);
      if (el) el.textContent = msg || '';
      if (input) {
        if (msg) { input.setAttribute('aria-invalid', 'true'); }
        else { input.removeAttribute('aria-invalid'); }
      }
    }
    function clearFieldErrors() {
      ['recesso-name', 'recesso-order', 'recesso-email', 'recesso-statement'].forEach(function (id) { setFieldError(id, ''); });
    }
    function showFormError(node, msg) {
      if (!node) return;
      node.textContent = msg || '';
      node.hidden = !msg;
    }
    function fieldMsg(code) {
      if (code === 'required') return t('required');
      if (code === 'invalid') return t('email');
      if (code === 'too_long') return t('tooLong');
      return t('generic');
    }
    function busy(btn, on) {
      if (!btn) return;
      btn.disabled = on;
      if (on) { btn.setAttribute('aria-busy', 'true'); } else { btn.removeAttribute('aria-busy'); }
    }

    function clientValidate() {
      clearFieldErrors();
      var ok = true;
      if (!nameEl.value.trim()) { setFieldError('recesso-name', t('required')); ok = false; }
      if (!orderEl.value.trim()) { setFieldError('recesso-order', t('required')); ok = false; }
      var em = emailEl.value.trim();
      if (!em) { setFieldError('recesso-email', t('required')); ok = false; }
      else if (!EMAIL_RE.test(em)) { setFieldError('recesso-email', t('email')); ok = false; }
      return ok;
    }

    function post(payload) {
      var ctrl = ('AbortController' in window) ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, FETCH_TIMEOUT_MS) : null;
      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
        signal: ctrl ? ctrl.signal : undefined
      }).then(function (res) {
        if (timer) clearTimeout(timer);
        return res.json().catch(function () { return {}; }).then(function (data) {
          return { status: res.status, data: data };
        });
      }).catch(function (err) {
        if (timer) clearTimeout(timer);
        throw err;
      });
    }

    function appendP(parent, text) {
      var p = document.createElement('p');
      p.textContent = text;
      parent.appendChild(p);
    }

    function renderSuccess(data) {
      var body = root.querySelector('#recesso-success-body');
      body.textContent = '';
      var email = emailEl.value.trim();
      var dt = data.confirmedAt ? new Date(data.confirmedAt) : new Date();
      var dtStr;
      try { dtStr = dt.toLocaleString(locale === 'it' ? 'it-IT' : 'en-GB'); } catch (e) { dtStr = dt.toISOString(); }
      appendP(body, t('transmittedAt', { datetime: dtStr }));
      if (data.referenceId) appendP(body, t('reference', { ref: data.referenceId }));
      appendP(body, data.receiptStatus === 'sent' ? t('receiptSent', { email: email }) : t('receiptPending', { email: email }));
      if (data.underReview) appendP(body, t('underReview'));
    }

    // STEP 1 — submit declaration → "recedere dal contratto qui" (c.3)
    step1.addEventListener('submit', function (ev) {
      ev.preventDefault();
      if (pending) return;
      showFormError(step1Error, '');
      if (!clientValidate()) return;

      pending = true;
      var submitBtn = step1.querySelector('button[type="submit"]');
      busy(submitBtn, true);

      post({
        action: 'submit',
        name: nameEl.value.trim(),
        orderNumber: orderEl.value.trim(),
        email: emailEl.value.trim(),
        statement: statementEl.value.trim() || undefined,
        locale: locale,
        source: 'recesso_page'
      }).then(function (r) {
        pending = false; busy(submitBtn, false);
        if (r.status === 201 && r.data && r.data.token) {
          draftToken = r.data.token;
          root.querySelector('#recesso-rv-name').textContent = nameEl.value.trim();
          root.querySelector('#recesso-rv-order').textContent = orderEl.value.trim();
          root.querySelector('#recesso-rv-email').textContent = emailEl.value.trim();
          var stRow = root.querySelector('#recesso-rv-statement-row');
          if (statementEl.value.trim()) {
            root.querySelector('#recesso-rv-statement').textContent = statementEl.value.trim();
            stRow.hidden = false;
          } else { stRow.hidden = true; }
          show(step2);
        } else if (r.status === 400 && r.data && r.data.errors) {
          var hadUnmapped = false;
          r.data.errors.forEach(function (e) {
            if (FIELD_MAP[e.field]) { setFieldError(FIELD_MAP[e.field], fieldMsg(e.code)); }
            else { hadUnmapped = true; }
          });
          if (hadUnmapped) showFormError(step1Error, t('generic'));
        } else if (r.status === 429) {
          showFormError(step1Error, t('rateLimited'));
        } else {
          showFormError(step1Error, t('generic'));
        }
      }).catch(function () {
        pending = false; busy(submitBtn, false);
        showFormError(step1Error, t('network'));
      });
    });

    // STEP 2 — confirm → "conferma recesso" (c.5)
    confirmBtn.addEventListener('click', function () {
      if (pending || !draftToken) return;
      showFormError(step2Error, '');
      pending = true; busy(confirmBtn, true);
      var original = confirmBtn.textContent;
      confirmBtn.textContent = t('sending');

      post({ action: 'confirm', token: draftToken }).then(function (r) {
        pending = false; busy(confirmBtn, false); confirmBtn.textContent = original;
        if (r.status === 200 && r.data && r.data.ok) {
          draftToken = null;
          renderSuccess(r.data);
          show(successPanel);
        } else if (r.status === 429) {
          showFormError(step2Error, t('rateLimited'));
        } else {
          // Generic, non-enumerating message; the support link is static in the markup.
          var msgEl = root.querySelector('[data-recesso-error-msg]');
          if (msgEl) msgEl.textContent = t('errorBody');
          show(errorPanel);
        }
      }).catch(function () {
        pending = false; busy(confirmBtn, false); confirmBtn.textContent = original;
        showFormError(step2Error, t('network'));
      });
    });

    // "Edit" — return to step 1. Invalidate the draft token so a stale confirm
    // can never fire, and the confirm control is disabled (handled by show()).
    backBtn.addEventListener('click', function () {
      draftToken = null;
      showFormError(step2Error, '');
      show(step1);
    });

    // Initial state: confirm disabled until step 2 is reached.
    if (confirmBtn) confirmBtn.disabled = true;
  }

  function boot() {
    var roots = document.querySelectorAll('.recesso[data-section-type="recesso"]');
    for (var i = 0; i < roots.length; i++) initRecesso(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
